import classnames from 'classnames'
import { Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { User } from '../../../models/User'
import useCreateGroup from '../../../state/mutations/useCreateGroup'
import useRoutes from '../../../state/useRoutes'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'
import SelectUsers from '../../formElements/SelectUsers/SelectUsers'

import styles from './CreateGroupForm.module.scss'

/**********************************************************************
 * CreateGroupForm
 **********************************************************************/
interface CreateGroupFormProps {
  onClose: () => void
}

export default React.memo(function CreateGroupForm({ onClose }: CreateGroupFormProps) {
  const { t } = useTranslation()

  const routes = useRoutes()

  const createGroup = useCreateGroup()

  const [selectedMembers, setSelectedMembers] = useState<User[]>([])
  const [selectedGroupManagers, setSelectedGroupManagers] = useState<User[]>([])

  return (
    <Formik
      initialValues={{ name: '', isManaged: false }}
      onSubmit={async (values, { setStatus }) => {
        try {
          const { uuid } = await createGroup.mutateAsync({
            ...values,
            users: selectedMembers.map(({ uuid }) => uuid),
            managers: selectedGroupManagers.map(({ uuid }) => uuid),
          })
          routes.manageGroup.goto({ uuid }, { replace: true })
        } catch (err) {
          setStatus(err)
        }
      }}
    >
      {(props) => (
        <form noValidate onSubmit={props.handleSubmit} className={classnames(styles.CreateGroupForm)}>
          <header>
            <hgroup>
              <h1>{t('manage group.create group')}</h1>
            </hgroup>
          </header>

          <section>
            <TextInputFormik name="name" label={t('group.group name')} required disabled={props.isSubmitting} />
          </section>

          <section className={styles.groupMembers}>
            <h1>{t('group.group managers')}</h1>
            <SelectUsers items={selectedGroupManagers} setItems={setSelectedGroupManagers} label={t('search users')} />
          </section>

          <section className={styles.groupMembers}>
            <h1>{t('group.group members')}</h1>
            <SelectUsers items={selectedMembers} setItems={setSelectedMembers} label={t('search users')} />
          </section>

          <FormButtons submitLabel={t('Create')} onClose={onClose} {...props} />
        </form>
      )}
    </Formik>
  )
})
